import { useEffect, useRef, useState } from 'react';

// --- Helper functions are unchanged ---
const applyBlurEffect = (ctx, results) => {
  const w = results.image.width;
  const h = results.image.height;
  ctx.save();
  ctx.clearRect(0, 0, w, h);
  ctx.drawImage(results.image, 0, 0, w, h);
  ctx.globalCompositeOperation = 'destination-in';
  ctx.drawImage(results.segmentationMask, 0, 0, w, h);
  ctx.globalCompositeOperation = 'destination-over';
  ctx.filter = 'blur(15px)';
  ctx.drawImage(results.image, 0, 0, w, h);
  ctx.restore();
};

const applyBackgroundImageEffect = (ctx, results, backgroundImage) => {
  const w = results.image.width;
  const h = results.image.height;
  ctx.save();
  ctx.clearRect(0, 0, w, h);
  ctx.drawImage(results.image, 0, 0, w, h);
  ctx.globalCompositeOperation = 'destination-in';
  ctx.drawImage(results.segmentationMask, 0, 0, w, h);
  ctx.globalCompositeOperation = 'destination-over';
  ctx.drawImage(backgroundImage, 0, 0, w, h);
  ctx.restore();
};


export const useCameraEffect = ({
  track,
  effect,
  videoRef,
  canvasRef,
  backgroundImageRef,
  isImageReady, // NEW: Explicit flag from the parent component
  onStreamReady,
  frameSkip = 1,
}) => {
  const segmenterRef = useRef(null);
  const animationFrameIdRef = useRef(null);
  const [error, setError] = useState(null); // NEW: error state

  // Effect 1: One-time setup and teardown of the segmenter.
  useEffect(() => {
    try {
      const segmenter = new window.SelfieSegmentation({
        locateFile: (file) => `/mediapipe/${file}`,
      });
      segmenter.setOptions({ modelSelection: 1 });
      segmenterRef.current = segmenter;
    } catch (err) {
      setError(err);
    }
    return () => {
      try {
        segmenterRef.current?.close();
        segmenterRef.current = null;
      } catch (err) {
        setError(err);
      }
    };
  }, []);

  // Effect 2: The main processing pipeline.
  useEffect(() => {
    let cleanupPipeline;
    try {
      const videoElement = videoRef.current;
      const canvasElement = canvasRef.current;
      const segmenter = segmenterRef.current;
      const backgroundImageElement = backgroundImageRef.current;

      cleanupPipeline = () => {
        try {
          if (animationFrameIdRef.current) {
            cancelAnimationFrame(animationFrameIdRef.current);
            animationFrameIdRef.current = null;
          }
          if (videoElement && videoElement.srcObject) {
            videoElement.pause();
            videoElement.srcObject = null;
          }
          if (segmenter) {
            segmenter.onResults(() => {});
          }
        } catch (err) {
          setError(err);
        }
      };

      const shouldRunEffect = () => {
        const prerequisites = {
          track: !!track,
          videoEl: !!videoElement,
          canvasEl: !!canvasElement,
          segmenter: !!segmenter,
          backgroundImage: !!backgroundImageElement,
          isImageReady,
          effect
        };
        if (!prerequisites.track || !prerequisites.videoEl || !prerequisites.canvasEl || !prerequisites.segmenter) return false;
        if (effect === 'blur') return true;
        if (effect === 'background' && backgroundImageElement && isImageReady) return true;
        return false;
      };

      const run = shouldRunEffect();

      if (run) {
        try {
          segmenter.onResults((results) => {
            try {
              const ctx = canvasElement.getContext('2d');
              if (!ctx || !results.image) return;

              if (canvasElement.width !== results.image.width || canvasElement.height !== results.image.height) {
                canvasElement.width = results.image.width;
                canvasElement.height = results.image.height;
              }

              if (effect === 'blur') {
                applyBlurEffect(ctx, results);
              } else if (effect === 'background') {
                applyBackgroundImageEffect(ctx, results, backgroundImageElement);
              }
            } catch (err) {
              setError(err);
            }
          });

          const handlePlaying = () => {
            let frameCount = 0;
            const sendFrame = async () => {
              try {
                if (videoElement.readyState >= 2) {
                  frameCount++;
                  if (frameCount % frameSkip === 0) {
                    await segmenter?.send({ image: videoElement });
                  }
                }
                animationFrameIdRef.current = requestAnimationFrame(sendFrame);
              } catch (err) {
                setError(err);
              }
            };
            sendFrame();
          };
          videoElement.addEventListener('playing', handlePlaying);

          videoElement.srcObject = new MediaStream([track]);
          videoElement.play();

          const outputStream = canvasElement.captureStream(track.getSettings().frameRate || 30);
          onStreamReady(outputStream);

          return () => {
            try {
              cleanupPipeline();
              videoElement.removeEventListener('playing', handlePlaying);
            } catch (err) {
              setError(err);
            }
          };
        } catch (err) {
          setError(err);
        }
      } else {
        try {
          cleanupPipeline();
          onStreamReady(track ? new MediaStream([track]) : null);
        } catch (err) {
          setError(err);
        }
      }
    } catch (err) {
      setError(err);
    }
    return cleanupPipeline;
  }, [track, effect, videoRef, canvasRef, backgroundImageRef, isImageReady, onStreamReady, frameSkip]);

  return { error };
};
